/**
 * Drives the Signify configuration sidebar: configurator session lifecycle
 * (create / punch-out / resume / insert) and the mapping table the user edits
 * before inserting.
 *
 * Invariant: mapping resolution is response-dependent, so both branches of
 * `onCreateConfiguration` MUST fetch price and availability — without it
 * `resolvedSourceMappings` has no input and the mapped-fields section renders
 * empty. Mapping config comes from `useSignifyMappings`; there are no defaults,
 * hence the `configDisabledReason` clause in `insertDisabledReason`.
 *
 * Session overrides are keyed by response path, so a destination a `constant`
 * source fills gets a read-only row and cannot be re-pointed here — the accepted
 * limit of SYD-2424.
 *
 * `selectedDistributor` and `distributorSearchValue` are derived, not mirrored:
 * an explicit choice wins, otherwise an open sidebar falls back to the first
 * loaded distributor. `typedDistributorSearch` is `null` whenever the user is not
 * actively typing, and only then does the box show the selected distributor's
 * name looked up in `allDistributors`. `''` is a real typed value — it keeps the
 * box clearable — so the fallback is `??`, never `||`. Selecting, opening and
 * auto-populating reset the typed value to `null` rather than writing a name into
 * it, which is what keeps a renamed distributor from showing stale text.
 *
 * Reads leaves off a narrow snapshot — never `useFormStore()`, whose spread of
 * `formVersion.data` re-rendered every host of this hook on each keystroke in
 * the form. `ArTableControls` mounts it unconditionally, so that spread was
 * repainting the whole table control bar while the user typed (SYD-2264). The
 * only version leaf read here is this table's own persisted Signify session.
 */
import { useState, useCallback, useContext, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { serverTimestamp } from 'firebase/firestore';
import { ProductSearchResult, DEFAULT_SIGNIFY_CUSTOMER_ID, SignifyConfigurationResponse } from '#types/SignifyApi.types';
import { TimeType, type Fields, type EntityRecord } from '#types/System.types';
import {
    indexSignifyProductPrices,
    REQUIRED_PRODUCT_ID_MAPPING,
    REQUIRED_SOURCE_ID_MAPPING,
    SIGNIFY_CONFIGURATION_FIELD_MAPPINGS
} from '#types';
import { MessageContext } from '@/providers/MessageContext';
import { UserContext } from '@/providers/UserContext';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { formStore } from '@/store';
import { useEvaluateFormAccess } from '@/use/data/useEvaluateFormAccess';
import {
    useSignifyDistributors,
    useSignifyProductSearch,
    useSignifyProductPriceAndAvailability,
    useSignifyConfiguredProducts,
    useSignifyConfigurationSession,
    useSignifyAutoConfigureProduct
} from '../../../api';
import { useSignifyMappings } from '../../../useSignifyMappings';
import { useSignifyProductFields } from '../../../useSignifyProductFields';
import { resolveSignifySourceMappings } from '../../../resolveSignifySourceMappings';
import { formatSignifySourceLabel } from '../../../formatSignifySourceLabel';
import { useCreateLineItemFromSignify } from '../../SignifyProductModal/use/useCreateLineItemFromSignify';
import type { MappingTableRow } from '../index.types';

interface UseSignifyConfigurationSidebarProps {
    tableId?: string;
    onSuccess?: (entityRecordId: string) => void;
    onManualInsert?: (recordName: string, fields: Fields, entityReferences: EntityRecord['entityReferences']) => void;
    disabled?: boolean;
}

export const useSignifyConfigurationSidebar = ({
    tableId,
    onSuccess,
    onManualInsert,
    disabled = false
}: UseSignifyConfigurationSidebarProps = {}) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [mode, setMode] = useState<'product' | 'catalog'>('product');
    const [customerId, setCustomerId] = useState(DEFAULT_SIGNIFY_CUSTOMER_ID);
    const [chosenDistributor, setChosenDistributor] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductSearchResult | null>(null);
    const [productSearchValue, setProductSearchValue] = useState('');
    const [typedDistributorSearch, setTypedDistributorSearch] = useState<string | null>(null);
    const [catalogCode, setCatalogCode] = useState('');
    const [createSession, setCreateSession] = useState(false);
    const [configurationResponse, setConfigurationResponse] = useState<SignifyConfigurationResponse | null>(null);
    const [configurableProductId, setConfigurableProductId] = useState<string | null>(null);
    const [saveRecord, setSaveRecord] = useState(false);
    const [loadingInsert, setLoadingInsert] = useState(false);
    const [lastDistributorId, setLastDistributorId] = useState('');
    const [customMappings, setCustomMappings] = useState<Record<string, string | null>>({});

    const { messageApi } = useContext(MessageContext);
    const { user, userRights, hasEntityAccess } = useContext(UserContext);
    const { fieldTypes } = useContext(FieldTypeContext);
    const formSnap = useSnapshot(formStore.state);
    const formVersionAccess = useEvaluateFormAccess({
        formId: formSnap.formId,
        versionId: formSnap.versionId,
        enabled: true,
    });
    const formReadOnly = !formVersionAccess.canWrite;
    const { availableProductFields } = useSignifyProductFields(isSidebarOpen);
    const { mappings: signifyMappings, configDisabledReason } = useSignifyMappings();

    const canWriteRecords = Boolean(userRights?.recordsWrite);

    const {
        distributors: filteredDistributors,
        allDistributors,
        loading: loadingDistributors,
        error: distributorError,
        searchTerm: distributorSearchTerm,
        setSearchTerm: setDistributorSearchTerm,
        refreshDistributors,
        totalCount: totalDistributors,
        filteredCount: filteredDistributorsCount
    } = useSignifyDistributors(DEFAULT_SIGNIFY_CUSTOMER_ID, isSidebarOpen);

    const {
        productOptions,
        searchingProducts,
        searchProducts,
        clearSearch
    } = useSignifyProductSearch({ configurable: 'true' });

    const {
        error: priceError,
        productPriceAndAvailabilityData,
        fetchProductPriceAndAvailability,
        clearProductPriceAndAvailability
    } = useSignifyProductPriceAndAvailability();

    const {
        loading: loadingCreate,
        error: createError,
        createConfiguredProducts,
        clearConfiguredProducts
    } = useSignifyConfiguredProducts();

    const {
        loading: loadingRetrieve,
        error: retrieveError,
        fetchConfigurationSession,
        clearConfigurationSession
    } = useSignifyConfigurationSession();

    const {
        loading: loadingAutoConfigure,
        error: autoConfigureError,
        fetchAutoConfiguredProduct,
        clearAutoConfigure
    } = useSignifyAutoConfigureProduct();

    const { createLineItemRecord, buildLineItemFields } = useCreateLineItemFromSignify();

    const error = createError || retrieveError || autoConfigureError || priceError;
    const distributorWarning = distributorError;

    const selectedDistributor = chosenDistributor || (isSidebarOpen ? allDistributors[0]?.uid ?? '' : '');
    const distributorSearchValue = typedDistributorSearch
        ?? allDistributors.find((d) => d.uid === selectedDistributor)?.name
        ?? '';

    const persistedSession = tableId && user?.uid
        ? formSnap.formVersion.data?.arTables?.[tableId]?.signifySessions?.[user.uid]
        : undefined;

    const hasActiveSession = Boolean(persistedSession);

    const persistSession = useCallback((response: SignifyConfigurationResponse) => {
        if (!tableId || !user?.uid) return;
        void formStore.getFormVersionPersist()?.setMerge({
            arTables: {
                [tableId]: {
                    signifySessions: {
                        [user.uid]: {
                            sessionId: response.sessionId ?? '',
                            punchOutUrl: response.punchOutUrl,
                            productId: response.productId ?? configurableProductId ?? '',
                            productCode: selectedProduct?.code,
                            productName: selectedProduct?.name,
                            distributorId: selectedDistributor,
                            mode,
                            catalogCode: mode === 'catalog' ? catalogCode : undefined,
                            complete: response.complete,
                            savedAt: serverTimestamp() as TimeType,
                            customMappings: Object.keys(customMappings).length > 0 ? customMappings : undefined,
                        },
                    },
                },
            },
        }, { debounce: true });
    }, [tableId, configurableProductId, selectedProduct?.code, selectedProduct?.name, selectedDistributor, mode, catalogCode, customMappings, user?.uid]);

    const clearPersistedSession = useCallback(() => {
        if (!tableId || !user?.uid) return;
        void formStore.getFormVersionPersist()?.setMerge({
            arTables: {
                [tableId]: {
                    signifySessions: {
                        [user.uid]: null,
                    },
                },
            },
        }, { debounce: true });
    }, [tableId, user?.uid]);

    const getDefaultDistributor = useCallback(() => {
        return allDistributors[0];
    }, [allDistributors]);

    const autoPopulateDefaultDistributor = useCallback(() => {
        if (!selectedDistributor) {
            const defaultDistributor = getDefaultDistributor();
            if (defaultDistributor) {
                setChosenDistributor(defaultDistributor.uid);
                setTypedDistributorSearch(null);
                setLastDistributorId(defaultDistributor.uid);
                return defaultDistributor;
            }
        }
        return null;
    }, [getDefaultDistributor, selectedDistributor]);

    const onCustomerIdChange = useCallback((value: string) => {
        setCustomerId(value);
    }, []);

    const onCreateSessionChange = useCallback((value: boolean) => {
        setCreateSession(value);
    }, []);

    const onCatalogCodeChange = useCallback((value: string) => {
        setCatalogCode(value);
    }, []);

    const onSaveRecordChange = useCallback((value: boolean) => {
        setSaveRecord(value);
    }, []);

    const openSidebar = useCallback(() => {
        if (persistedSession && !configurationResponse) {
            setConfigurationResponse({
                sessionId: persistedSession.sessionId,
                punchOutUrl: persistedSession.punchOutUrl,
                productId: persistedSession.productId,
                complete: persistedSession.complete,
            });
            setChosenDistributor(persistedSession.distributorId);
            setMode(persistedSession.mode);
            if (persistedSession.catalogCode) {
                setCatalogCode(persistedSession.catalogCode);
            }
            if (persistedSession.productId) {
                setConfigurableProductId(persistedSession.productId);
            }
            if (persistedSession.productName) {
                setProductSearchValue(persistedSession.productName);
                setSelectedProduct({
                    code: persistedSession.productCode ?? persistedSession.productId,
                    name: persistedSession.productName,
                    commercialCode: '',
                    codeForDisplay: '',
                    url: '',
                } as ProductSearchResult);
            }
            if (persistedSession.customMappings && Object.keys(persistedSession.customMappings).length > 0) {
                setCustomMappings(persistedSession.customMappings);
            }
        }
        if (!selectedDistributor && !persistedSession) {
            if (lastDistributorId) {
                setChosenDistributor(lastDistributorId);
            } else {
                const defaultDistributor = getDefaultDistributor();
                if (defaultDistributor) {
                    setChosenDistributor(defaultDistributor.uid);
                    setLastDistributorId(defaultDistributor.uid);
                }
            }
            setTypedDistributorSearch(null);
        }
        setIsSidebarOpen(true);
    }, [configurationResponse, getDefaultDistributor, lastDistributorId, persistedSession, selectedDistributor]);

    const closeSidebar = useCallback(() => {
        clearSearch();
        clearProductPriceAndAvailability();
        clearConfiguredProducts();
        clearConfigurationSession();
        clearAutoConfigure();
        setIsSidebarOpen(false);
    }, [
        clearSearch,
        clearProductPriceAndAvailability,
        clearConfiguredProducts,
        clearConfigurationSession,
        clearAutoConfigure
    ]);

    const clearConfiguration = useCallback(() => {
        setSelectedProduct(null);
        setProductSearchValue('');
        setConfigurableProductId(null);
        setCatalogCode('');
        setConfigurationResponse(null);
        setCustomMappings({});
        clearSearch();
        clearProductPriceAndAvailability();
        clearConfiguredProducts();
        clearConfigurationSession();
        clearAutoConfigure();
        clearPersistedSession();
    }, [
        clearSearch,
        clearProductPriceAndAvailability,
        clearConfiguredProducts,
        clearConfigurationSession,
        clearAutoConfigure,
        clearPersistedSession
    ]);

    /**
     * Resolves the configured rules against the response the configured product
     * returned and presents them source-first, so each row binds to whichever
     * source this brand actually supplies.
     */
    const resolvedSourceMappings = useMemo(() => resolveSignifySourceMappings({
        fieldMappings: signifyMappings.fieldMappings,
        response: productPriceAndAvailabilityData
            ? indexSignifyProductPrices(productPriceAndAvailabilityData)
            : {},
        availableFieldIds: availableProductFields.map(f => f.fieldId)
    }), [availableProductFields, productPriceAndAvailabilityData, signifyMappings]);

    const getFieldDisplayName = useCallback(
        (fieldId: string) => {
            const field = availableProductFields.find(f => f.fieldId === fieldId);
            if (field) return field.label;
            const ft = fieldTypes[fieldId];
            return ft?.description?.shortLabel || fieldId;
        },
        [availableProductFields, fieldTypes]
    );

    const sectionHeader = (key: string, label: string, category: MappingTableRow['category']): MappingTableRow => ({
        key,
        category,
        apiField: '',
        apiFieldDisplay: label,
        lineItemField: '',
        lineItemFieldDisplay: '',
        editable: false,
        sectionHeader: true,
    });

    const mappingTableData = useMemo((): MappingTableRow[] => {
        const rows: MappingTableRow[] = [];

        rows.push(sectionHeader('section-mapped', 'Mapped Fields', 'mapped'));
        Object.entries(resolvedSourceMappings.sourceMappings).forEach(([apiField, lineItemField]) => {
            const effective = customMappings[apiField] !== undefined
                ? customMappings[apiField]
                : lineItemField;
            rows.push({
                key: `mapped-${apiField}`,
                category: 'mapped',
                apiField,
                apiFieldDisplay: formatSignifySourceLabel(apiField),
                lineItemField: effective ?? '',
                lineItemFieldDisplay: effective ? getFieldDisplayName(effective) : '',
                editable: true,
                customized: customMappings[apiField] !== undefined
            });
        });

        rows.push(sectionHeader('section-constant', 'Set From Configuration', 'constant'));
        resolvedSourceMappings.constants.forEach(({ fieldId, value }) => {
            rows.push({
                key: `constant-${fieldId}`,
                category: 'constant',
                apiField: 'constant',
                apiFieldDisplay: `"${String(value)}"`,
                lineItemField: fieldId,
                lineItemFieldDisplay: getFieldDisplayName(fieldId),
                editable: false
            });
        });

        rows.push(sectionHeader('section-config', 'Configuration', 'configuration'));
        Object.entries(SIGNIFY_CONFIGURATION_FIELD_MAPPINGS).forEach(([apiField, lineItemField]) => {
            rows.push({
                key: `config-${apiField}`,
                category: 'configuration',
                apiField,
                apiFieldDisplay: apiField,
                lineItemField,
                lineItemFieldDisplay: getFieldDisplayName(lineItemField),
                editable: false
            });
        });

        rows.push(sectionHeader('section-required', 'Required', 'required'));
        const [requiredApiField, requiredLineItemField] = Object.entries(REQUIRED_PRODUCT_ID_MAPPING)[0];
        rows.push({
            key: `required-${requiredApiField}`,
            category: 'required',
            apiField: requiredApiField,
            apiFieldDisplay: requiredApiField,
            lineItemField: requiredLineItemField,
            lineItemFieldDisplay: getFieldDisplayName(requiredLineItemField),
            editable: false
        });
        rows.push({
            key: 'required-manufacturersSourceId',
            category: 'required',
            apiField: 'constant',
            apiFieldDisplay: `"${REQUIRED_SOURCE_ID_MAPPING}"`,
            lineItemField: 'manufacturersSourceId',
            lineItemFieldDisplay: getFieldDisplayName('manufacturersSourceId'),
            editable: false
        });

        return rows;
    }, [resolvedSourceMappings, customMappings, getFieldDisplayName]);

    const onMappingChange = useCallback(
        (apiField: string, tableColumn: string | null) => {
            const originalMapping = resolvedSourceMappings.sourceMappings[apiField];

            if (tableColumn) {
                setCustomMappings((prev) => ({
                    ...prev,
                    [apiField]: tableColumn,
                }));
            } else {
                if (originalMapping) {
                    setCustomMappings((prev) => ({
                        ...prev,
                        [apiField]: null,
                    }));
                } else {
                    setCustomMappings((prev) => {
                        const updated = { ...prev };
                        delete updated[apiField];
                        return updated;
                    });
                }
            }
        },
        [resolvedSourceMappings]
    );

    const onProductSearch = useCallback((value: string) => {
        setProductSearchValue(value);
        searchProducts(value);
    }, [searchProducts]);

    const onProductSelect = useCallback(async (value: string) => {
        const product = productOptions.find((p) => p.code === value);
        if (product) {
            if (configurationResponse) {
                setConfigurationResponse(null);
                setCustomMappings({});
                clearConfiguredProducts();
                clearConfigurationSession();
                clearAutoConfigure();
                clearProductPriceAndAvailability();
                clearPersistedSession();
            }

            setSelectedProduct(product);
            setProductSearchValue(
                product.commercialCode
                    ? `${product.name} (${product.commercialCode})`
                    : product.name
            );

            if (product.configurable) {
                setConfigurableProductId(product.code);
            } else if (product.configurableProduct) {
                setConfigurableProductId(product.configurableProduct);
            } else {
                setConfigurableProductId(null);
            }
        }
    }, [clearAutoConfigure, clearConfiguredProducts, clearConfigurationSession, clearPersistedSession, clearProductPriceAndAvailability, configurationResponse, productOptions]);

    const onProductClear = useCallback(() => {
        setSelectedProduct(null);
        setProductSearchValue('');
        setConfigurableProductId(null);
    }, []);

    const onDistributorSearch = useCallback((value: string) => {
        setTypedDistributorSearch(value);
        setDistributorSearchTerm(value);
    }, [setDistributorSearchTerm]);

    const onDistributorSelect = useCallback((value: string) => {
        setChosenDistributor(value);
        const distributor = allDistributors.find((d) => d.uid === value);
        if (distributor) {
            setTypedDistributorSearch(null);
            setLastDistributorId(value);
        }
    }, [allDistributors]);

    const onDistributorClear = useCallback(() => {
        setChosenDistributor('');
        setTypedDistributorSearch(null);
        setLastDistributorId('');
    }, []);

    const onCreateConfiguration = useCallback(async () => {
        clearProductPriceAndAvailability();
        clearSearch();
        let distributorToUse = selectedDistributor;
        if (!distributorToUse) {
            const defaultDistributor = autoPopulateDefaultDistributor();
            if (defaultDistributor) {
                distributorToUse = defaultDistributor.uid;
            }
        }

        if (!distributorToUse) return;

        if (mode === 'product') {
            if (!configurableProductId) return;
            const response = await createConfiguredProducts(
                customerId,
                distributorToUse,
                configurableProductId,
                'FULL'
            );
            setConfigurationResponse(response);
            persistSession(response);
            if (response.punchOutUrl) {
                window.open(response.punchOutUrl, '_blank', 'noopener');
            }

            const configuredProductId = response.productId;
            if (configuredProductId) {
                try {
                    await fetchProductPriceAndAvailability(configuredProductId, 'FULL', customerId, distributorToUse, response.sessionId);
                } catch (err) {
                    console.warn('[Signify] Price fetch failed:', err);
                }
            }
            return;
        }

        if (!catalogCode.trim()) return;
        const response = await fetchAutoConfiguredProduct(
            customerId,
            distributorToUse,
            catalogCode.trim(),
            createSession,
            'FULL'
        );
        setConfigurationResponse(response);
        persistSession(response);

        const productId = response.productId;
        if (productId) {
            try {
                await fetchProductPriceAndAvailability(productId, 'FULL', customerId, distributorToUse, response.sessionId);
            } catch (err) {
                console.warn('[Signify] Price fetch failed:', err);
            }
        }
    }, [
        autoPopulateDefaultDistributor,
        catalogCode,
        clearProductPriceAndAvailability,
        clearSearch,
        configurableProductId,
        createConfiguredProducts,
        createSession,
        customerId,
        fetchAutoConfiguredProduct,
        fetchProductPriceAndAvailability,
        mode,
        persistSession,
        selectedDistributor
    ]);

    const onOpenPunchOut = useCallback(() => {
        if (!configurationResponse?.punchOutUrl) return;
        window.open(configurationResponse.punchOutUrl, '_blank', 'noopener');
    }, [configurationResponse]);

    const insertDisabledReason = useMemo(() => {
        if (disabled || formReadOnly || !hasEntityAccess) {
            return 'You do not have permission to insert.';
        }
        if (!configurationResponse?.sessionId) {
            return 'No active configuration session.';
        }
        if (configDisabledReason) {
            return configDisabledReason;
        }
        return null;
    }, [
        configDisabledReason,
        configurationResponse?.sessionId,
        disabled,
        formReadOnly,
        hasEntityAccess,
    ]);

    const onInsert = useCallback(async () => {
        if (insertDisabledReason) return;
        const sessionId = configurationResponse?.sessionId;
        if (!sessionId) return;

        setLoadingInsert(true);
        try {
            clearProductPriceAndAvailability();
            clearSearch();

            let distributorToUse = selectedDistributor;
            if (!distributorToUse) {
                const defaultDistributor = autoPopulateDefaultDistributor();
                if (defaultDistributor) {
                    distributorToUse = defaultDistributor.uid;
                }
            }
            if (!distributorToUse) return;

            const response = await fetchConfigurationSession(
                customerId,
                distributorToUse,
                sessionId,
                'FULL'
            );
            setConfigurationResponse(response);
            persistSession(response);

            if (response.complete === false) {
                messageApi.warning('Configuration is not complete. Please finish configuring in the Signify configurator first.');
                return;
            }

            const productId = response.productId || selectedProduct?.code;
            let priceData = null;
            if (productId) {
                try {
                    priceData = await fetchProductPriceAndAvailability(productId, 'FULL', customerId, distributorToUse, sessionId);
                } catch (err) {
                    console.warn('[Signify] Price fetch failed:', err);
                }
            }

            const variableAssignmentsValue = response.variableAssignments
                ? JSON.stringify(response.variableAssignments, null, 2)
                : null;
            const configuration = {
                sessionId: response.sessionId ?? null,
                commercialCode: response.commercialCode ?? null,
                complete: response.complete ?? null,
                variableAssignments: variableAssignmentsValue
            };

            const productAndPrice = priceData || {
                product: { code: productId ?? '', name: '', commercialCode: '', codeForDisplay: '', url: '' },
                price: null,
                availability: null,
                defaultPlannedDeliveryTime: null,
            };
            const payload = { ...productAndPrice, configuration };

            /**
             * Inverted against the payload being written, not against the memo the
             * rows render from: the price data is fetched inside this handler, so at
             * render time the response may still have been empty. Same function, same
             * available fields, so a row and its override always name one destination.
             */
            const sessionMappings = {
                customMappings,
                surfacedDestinations: resolveSignifySourceMappings({
                    fieldMappings: signifyMappings.fieldMappings,
                    response: indexSignifyProductPrices(payload),
                    availableFieldIds: availableProductFields.map(f => f.fieldId),
                }).sourceMappings,
            };

            if (saveRecord && onSuccess) {
                const { recordId, status } = await createLineItemRecord(payload, sessionMappings);
                if (status === 'duplicate') {
                    messageApi.warning('Multiple Line Items found with this product ID - please review your records');
                }
                clearPersistedSession();
                onSuccess(recordId);
            } else if (onManualInsert) {
                const { fields, recordName, entityReferences } = await buildLineItemFields(payload, sessionMappings);
                clearPersistedSession();
                onManualInsert(recordName, fields, entityReferences);
            }
            setSelectedProduct(null);
            setProductSearchValue('');
            setConfigurableProductId(null);
            setCatalogCode('');
            setConfigurationResponse(null);
            closeSidebar();
        } catch (err) {
            setConfigurationResponse(null);
            clearPersistedSession();
            messageApi.error('Failed to insert configuration.');
            throw err;
        } finally {
            setLoadingInsert(false);
        }
    }, [
        autoPopulateDefaultDistributor,
        buildLineItemFields,
        clearPersistedSession,
        clearProductPriceAndAvailability,
        clearSearch,
        closeSidebar,
        configurationResponse?.sessionId,
        createLineItemRecord,
        customMappings,
        customerId,
        fetchConfigurationSession,
        fetchProductPriceAndAvailability,
        insertDisabledReason,
        messageApi,
        onManualInsert,
        availableProductFields,
        onSuccess,
        persistSession,
        signifyMappings,
        saveRecord,
        selectedDistributor,
        selectedProduct?.code,
    ]);

    return {
        openSidebar,
        open: isSidebarOpen,
        onClose: closeSidebar,
        mode,
        customerId,
        customerIdDisabled: true,
        onCustomerIdChange,
        createSession,
        onCreateSessionChange,
        selectedDistributor,
        distributorSearchValue,
        filteredDistributors,
        filteredDistributorsCount,
        totalDistributors,
        distributorSearchTerm,
        loadingDistributors,
        selectedProduct,
        configurableProductId,
        productSearchValue,
        productOptions,
        searchingProducts,
        catalogCode,
        punchOutUrl: configurationResponse?.punchOutUrl,
        sessionId: configurationResponse?.sessionId,
        onCreateConfiguration,
        onOpenPunchOut,
        onInsert,
        loadingCreate: loadingCreate || loadingAutoConfigure,
        loadingRetrieve,
        loadingInsert,
        error,
        distributorWarning,
        insertDisabledReason,
        saveRecord,
        onSaveRecordChange,
        canWriteRecords,
        onProductSearch,
        onProductSelect,
        onProductClear,
        onDistributorSearch,
        onDistributorSelect,
        onDistributorClear,
        onRefreshDistributors: refreshDistributors,
        onCatalogCodeChange,
        hasActiveSession,
        onClearConfiguration: clearConfiguration,
        customMappings,
        onMappingChange,
        mappingTableData,
        availableLineItemFields: availableProductFields
    };
};
